import {merge, combineLatest} from 'rxjs';
import {filter, map, scan} from 'rxjs/operators';
import {wsLogout$} from '@/user/auth-ws';
import {contractAccountCurrencies$, riskEngineMode$} from '@/config';
import {aopMsgAll$, aopMsgV2All$} from '@/raw/aop-all';
import {subUserIds$} from '@/raw/users';
import {rasAccountSubUsers$} from '@/ras/all/ras-cache-all';
import {convertAccountV2} from '../contract-accounts-cache';
import {accountPosTotalDataCacheAll$} from '@/positions/all/calc';
import type {ws, ReduceAction} from '@/types';

type ContractAccountRaw = ws.ContractAccountRaw;
type Cache = Map<string, ContractAccountRaw | ws.ContractAccountRawV2>;
type CacheAll = Map<number, Cache>;
type Action = ReduceAction<Cache>;
type ActionAll = ReduceAction<CacheAll>;

const accountInitAll$ = combineLatest([subUserIds$, contractAccountCurrencies$]).pipe(
  map(([userIds, accountCurrencies]): ActionAll => {
    const action = function (cache: Cache) {
      return Array.from(new Set(accountCurrencies)).reduce((p, currency) => {
        if (p.has(currency)) {
          return p;
        }
        return p.set(currency, {
          accountBalanceEv: 0,
          accountID: 0,
          bonusBalanceEv: 0,
          currency,
          totalUsedBalanceEv: 0,
          userID: 0,
          tradeLevel: 0,
          isolatedTotalBalanceEv: 0,
          userMode: null,
          totalDebtEv: 0,
          totalInterestEv: 0,
        });
      }, cache);
    };
    return (cacheAll: CacheAll) =>
      userIds.reduce((acc, userId) => (acc.has(userId) ? acc : acc.set(userId, action(new Map()))), cacheAll);
  })
);

const accountResetAll$ = wsLogout$.pipe(
  map(() => (cache: CacheAll) => {
    cache.clear();
    return cache;
  })
);

function wrapActionByUser(userId: number, action: Action) {
  return function (cache: CacheAll) {
    const userCache = cache.get(userId) || new Map();
    cache.set(userId, action(userCache));
    return cache;
  };
}

const accountUpdate$ = aopMsgAll$.pipe(
  filter((o) => !!o.data.accounts),
  map((aop) => {
    const action = function (cache: Cache) {
      aop.data.accounts.forEach((account: ContractAccountRaw) => cache.set(account.currency, account));
      return cache;
    };
    return wrapActionByUser(aop.userId, action);
  })
);

const accountUpdate2$ = aopMsgV2All$.pipe(
  filter((o) => !!o.data.accounts),
  map((aop) => {
    const action = function (cache: Cache) {
      aop.data.accounts.forEach((account: ws.ContractAccountRawV2) => cache.set(account.currency, account));
      return cache;
    };
    return wrapActionByUser(aop.userId, action);
  })
);

const innerContractAccountsCacheSubs$ = merge<ActionAll[]>(
  accountInitAll$,
  accountUpdate$,
  accountUpdate2$,
  accountResetAll$
).pipe(scan((p, action) => action(p), new Map() as CacheAll));

export const contractAccountsCacheSubs$ = combineLatest([
  innerContractAccountsCacheSubs$,
  riskEngineMode$,
  rasAccountSubUsers$,
  accountPosTotalDataCacheAll$,
]).pipe(
  map(([innerContractAccountsCacheSubs, riskEngineMode, rasAccountSubUsers, accountPosTotalDataCacheAll]) => {
    const cache = new Map();
    innerContractAccountsCacheSubs.forEach((accountsCache, key) => {
      cache.set(
        key,
        convertAccountsV2(key, accountsCache, riskEngineMode, rasAccountSubUsers, accountPosTotalDataCacheAll)
      );
    });
    return cache;
  })
);

function convertAccountsV2(userId, accountsCache, riskEngineMode, rasAccountSubUsers, accountPosTotalDataCacheAll) {
  const rasAccountMap = rasAccountSubUsers.get(userId) ? rasAccountSubUsers.get(userId) : new Map();
  const accountPosTotalDataCache = accountPosTotalDataCacheAll.get(userId) || null;
  const accounts = accountsCache.toArray();
  const accountsV2 = accounts.filter((o) => ['USDT', 'USDC', 'MUSDT'].includes(o.currency));
  const accountsV1 = accounts.filter((o) => !['USDT', 'USDC', 'MUSDT'].includes(o.currency));
  return accountsV1
    .concat(convertAccountV2([accountsV2, riskEngineMode, rasAccountMap, accountPosTotalDataCache]))
    .reduce((p, c) => p.set(c.currency, c), new Map());
}
